import { useEffect } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { format } from 'date-fns'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { FormContainer } from 'react-hook-form-mui'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import ColorUtils from '~/features/shared/utils/color.util'
import { getDateMaskFormat } from '~/utils/format-date'

import { useUpdateDynamicLink } from '../hooks/useDynamicLinks'
import { type SurveyDynamicModel } from '../types/survey-dynamic-model.type'
import { urlFromSurvey } from '../utils/dynamic-link-generator'

interface DynamicEditModalProps {
  survey: SurveyDynamicModel
  isOpenModal: boolean
  onCloseModal: () => void
  surveyDefinitionTitle?: string
}

interface FormValues {
  toDate: moment.Moment | null
}

export default function DynamicLinkEditModal({
  isOpenModal,
  onCloseModal,
  survey,
  surveyDefinitionTitle,
}: DynamicEditModalProps) {
  const { t } = useTranslation()
  const updateMutation = useUpdateDynamicLink()

  const validationSchema = z.object({
    toDate: z
      .custom<moment.Moment>(value => moment.isMoment(value))
      .nullable()
      .transform((val, ctx) => {
        if (!val) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('views.dynamicLinks.errors.toDateRequired'),
          })
          return z.NEVER
        }
        return val
      })
      .refine(val => moment().isSameOrBefore(val), t('validation.validDate')),
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues: {
      toDate: null,
    },
  })

  useEffect(() => {
    if (survey?.toDate) {
      const date = moment(survey.toDate)
      form.reset({ toDate: date })
    }
  }, [survey, form])

  function onSubmit(formValues: FormValues) {
    if (!survey?.id || !formValues.toDate) return

    // Convert to LocalDateTime format (without timezone 'Z')
    // Backend expects format like "2026-01-27T21:14:00" that LocalDateTime.parse() can handle
    const isoDate = format(formValues.toDate.toDate(), "yyyy-MM-dd'T'HH:mm:ss")

    updateMutation.mutate(
      {
        id: survey.id,
        status: survey.status ?? 'ACTIVE',
        toDate: isoDate,
      },
      {
        onSuccess: () => {
          handleClose()
        },
      },
    )
  }

  function handleClose() {
    form.reset({ toDate: null })
    onCloseModal()
  }

  const generatedUrl = urlFromSurvey(survey)

  return (
    <Dialog open={isOpenModal} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6">
            {t('views.dynamicLinks.editLink')}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <FormContainer formContext={form} onSuccess={onSubmit}>
        <DialogContent dividers>
          <Stack spacing={3}>
            {updateMutation.isError && (
              <Alert severity="error">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : t('views.dynamicLinks.errors.updateFailed')}
              </Alert>
            )}

            {/* Read-only information */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t('views.dynamicLinks.linkDetails')}
              </Typography>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('views.dynamicLinks.surveyDefinition')}
                  </Typography>
                  <Typography variant="body2">
                    {surveyDefinitionTitle ?? survey.surveyDefinition}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    {t('views.dynamicLinks.status')}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={survey.status}
                      color={ColorUtils.dynamicLinkStatusToChipColor(
                        survey.status,
                      )}
                      size="small"
                    />
                  </Box>
                </Box>

                {survey.createdBy && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('views.dynamicLinks.createdBy')}
                    </Typography>
                    <Typography variant="body2">{survey.createdBy}</Typography>
                  </Box>
                )}

                {survey.createdDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('views.dynamicLinks.createdDate')}
                    </Typography>
                    <Typography variant="body2">
                      {format(new Date(survey.createdDate), 'PPpp')}
                    </Typography>
                  </Box>
                )}

                {generatedUrl && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('views.dynamicLinks.url')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        wordBreak: 'break-all',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                      }}
                    >
                      {generatedUrl}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Editable field */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                {t('views.dynamicLinks.editableFields')}
              </Typography>
              <Controller
                name="toDate"
                control={form.control}
                render={({ field }) => (
                  <DateTimePicker
                    {...field}
                    label={t('views.dynamicLinks.expirationDate')}
                    minDateTime={moment()}
                    format={getDateMaskFormat()}
                    ampm={false}
                    disabled={updateMutation.isLoading}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(form.formState.errors.toDate),
                        helperText: form.formState.errors.toDate?.message,
                        onBlur: () => {
                          field.onBlur()
                        },
                      },
                      actionBar: {
                        actions: ['clear', 'cancel', 'accept', 'today'],
                      },
                    }}
                  />
                )}
              />
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={updateMutation.isLoading}
          >
            {t('views.dynamicLinks.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={updateMutation.isLoading}
          >
            {updateMutation.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              t('views.dynamicLinks.update')
            )}
          </Button>
        </DialogActions>
      </FormContainer>
    </Dialog>
  )
}
